import React, { useState, useEffect } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import Message from '../components/Message'
import ToastMessage from '../components/ToastMessage'
import StudyEditBlockingOverlay from '../components/StudyEditBlockingOverlay'
import useStudyEditBeforeUnload from '../hooks/useStudyEditBeforeUnload'
import SystemOwnerStudyBasic from '../components/SystemOwnerStudyBasic'
import SystemOwnerStudyConsent from '../components/SystemOwnerStudyConsent'
import SystemOwnerStudyStage from '../components/SystemOwnerStudyStage'

import { Row, Col, Card, Button, Form } from 'react-bootstrap'

import {
  addStage,
  editStudyExceptConsent,
  listStudyDetails,
  createSettingsSet,
  updateSettingsSet,
  setDefaultSettingsSet,
  deleteSettingsSet,
} from '../actions/systemOwnerStudyActions'
import { getQxList } from '../../qx_module/actions/systemOwnerQxActions'
import { getTxPageList } from '../../tx_materials_module/actions/txMaterialsActions'
import {
  PARTI_GROUP_ADD_RESET,
  PARTI_GROUP_DELETE_RESET,
  STAGE_ADD_RESET,
  STAGE_DELETE_RESET,
  STUDY_DETAILS_RESET,
  STUDY_EDIT_CONSENT_RESET,
  STUDY_EDIT_EXCEPT_CONSENT_RESET,
  SETTINGS_SET_MUTATION_RESET,
} from '../constants/studyConstants'
import resetErrorState from '../utils/resetErrorState'
import { getJitaiMessageList, getJitaiVariableList, getJitaiDecisionRuleList, getJitaiRespList } from '../../jitai_module/actions/jitaiActions'

const SystemOwnerStudyEditScreen = ({ match, history }) => {
  const managedByUserId = match.params.userId
  if (!managedByUserId) {
    history.push('/systemowner/login')
  }

  // For toast message
  const [toastMessages, setToastMessages] = useState([])

  const toastOnCloseHandler = async (uid) => {
    setToastMessages((toastMessages) =>
      toastMessages.filter((message) => message.uid !== uid)
    )
  }

  const studyId = match.params.id

  const [isSaving, setIsSaving] = useState(false)
  // Which settings set is currently being edited. undefined => study default set.
  const [selectedSettingsSetId, setSelectedSettingsSetId] = useState(undefined)
  const [study, setStudy] = useState({
    _id: '',
    study_code: '',
    active_status: '',
    chin_name: '',
    contact_person_name: '',
    contact_email: '',
    stage_list: [
      {
        chin_name: '',
        stage_num: 1,
        consent_info: {
          consent_needed: true,
          chin_content: '',
        },
        chin_complete_message: '',
        //participant_group was previously named as ax_version
        participant_group_list: [
          {
            name: '',
            group_num: 1,
            ema_list: [],
            diary_list: [],
            questionnaire_list: [],
          },
        ],
      },
    ],
  })

  const dispatch = useDispatch()

  //It is needed to check if the logged in user is a system owner
  const systemOwnerLogin = useSelector((state) => state.systemOwnerLogin)
  const { systemOwnerInfo, logout } = systemOwnerLogin

  const studyDetails = useSelector((state) => state.studyDetails)
  const {
    loading: loadingInitiateStudy,
    error: errorInitiateStudy,
    study: updatedStudy,
    success: successInitiateStudy,
  } = studyDetails

  ////studyEditConsent state can be altered after form in StudyConsent componet is submitted
  const studyEditConsent = useSelector((state) => state.studyEditConsent)
  const { error: errorEditConsent, success: successEditConsent } =
    studyEditConsent

  const stageAdd = useSelector((state) => state.stageAdd)
  const { error: errorStageAdd, success: successStageAdd } = stageAdd
  const stageDelete = useSelector((state) => state.stageDelete)
  const { error: errorStageDelete, success: successStageDelete } = stageDelete

  const participantGroupAdd = useSelector((state) => state.participantGroupAdd)
  const {
    error: errorParticipantGroupAdd,
    success: successParticipantGroupAdd,
  } = participantGroupAdd
  const participantGroupDelete = useSelector(
    (state) => state.participantGroupDelete
  )
  const {
    error: errorParticipantGroupDelete,
    success: successParticipantGroupDelete,
  } = participantGroupDelete

  const qxList = useSelector((state) => state.qxList)
  const {
    loading: loadingQxList,
    error: errorQxList,
    qx_list: initialQxList,
    success: successQxList,
  } = qxList

  const studyEditExceptConsent = useSelector(
    (state) => state.studyEditExceptConsent
  )
  const {
    loading: loadingEditStudy,
    error: errorEditStudy,
    success: successEditStudy,
  } = studyEditExceptConsent

  const settingsSetMutation = useSelector((state) => state.settingsSetMutation)
  const {
    loading: loadingSettingsSetMutation,
    error: errorSettingsSetMutation,
    success: successSettingsSetMutation,
  } = settingsSetMutation

  // JITAI data
  const jitaiMessageList = useSelector((state) => state.jitaiMessageList)
  const { 
    loading: loadingJitaiMessages,
    error: errorJitaiMessages,
    success: successJitaiMessages 
  } = jitaiMessageList
  
  const jitaiVariableList = useSelector((state) => state.jitaiVariableList)
  const { 
    loading: loadingJitaiVariables,
    error: errorJitaiVariables,
    success: successJitaiVariables 
  } = jitaiVariableList
  
  const jitaiDecisionRuleList = useSelector((state) => state.jitaiDecisionRuleList)
  const { 
    loading: loadingJitaiDecisionRules,
    error: errorJitaiDecisionRules,
    success: successJitaiDecisionRules 
  } = jitaiDecisionRuleList

  const jitaiRespListState = useSelector((state) => state.jitaiRespList)
  const { 
    loading: loadingJitaiResps,
    error: errorJitaiResps,
    success: successJitaiResps 
  } = jitaiRespListState

  // TX Materials data
  const txPageList = useSelector((state) => state.txPageList)
  const {
    loading: loadingTxPageList,
    error: errorTxPageList,
    success: successTxPageList
  } = txPageList

  useEffect(() => {
    const now = new Date()
    if (!systemOwnerInfo || systemOwnerInfo.expires < now || logout) {
      dispatch({ type: STUDY_DETAILS_RESET })
      history.push('/systemowner/login')
    } else {
      if (!loadingQxList && !errorQxList && !successQxList) {
        dispatch(getQxList(managedByUserId))
      }

      // Fetch JITAI data for the study (only once at screen level)
      if (!loadingJitaiMessages && !errorJitaiMessages && !successJitaiMessages) {
        dispatch(getJitaiMessageList(studyId))
      }
      if (!loadingJitaiVariables && !errorJitaiVariables && !successJitaiVariables) {
        dispatch(getJitaiVariableList(studyId))
      }
      if (!loadingJitaiDecisionRules && !errorJitaiDecisionRules && !successJitaiDecisionRules) {
        dispatch(getJitaiDecisionRuleList(studyId))
      }
      if (!loadingJitaiResps && !errorJitaiResps && !successJitaiResps) {
        dispatch(getJitaiRespList(studyId))
      }

      // Fetch TX materials data for the study (only once at screen level)
      if (!loadingTxPageList && !errorTxPageList && !successTxPageList) {
        dispatch(getTxPageList(studyId))
      }

      if (!loadingInitiateStudy && !errorInitiateStudy) {
        if (!successInitiateStudy) {
          resetErrorState(dispatch)
          dispatch(listStudyDetails(studyId, selectedSettingsSetId))
        } else {
          setStudy(updatedStudy)
        }
      }

      if (successEditConsent) {
        window.scrollTo(0, 0)
        setToastMessages((toastMessages) => [
          ...toastMessages,
          {
            uid: new Date().valueOf(),
            variant: 'info',
            message: 'Consent Form Updated',
          },
        ])
        dispatch({ type: STUDY_EDIT_CONSENT_RESET })
      }

      if (successStageAdd) {
        window.scrollTo(0, 0)
        setToastMessages((toastMessages) => [
          ...toastMessages,
          {
            uid: new Date().valueOf(),
            variant: 'info',
            message: 'Stage Added',
          },
        ])
        dispatch({ type: STAGE_ADD_RESET })
      }

      if (successStageDelete) {
        window.scrollTo(0, 0)
        setToastMessages((toastMessages) => [
          ...toastMessages,
          {
            uid: new Date().valueOf(),
            variant: 'info',
            message: 'Stage Deleted',
          },
        ])
        dispatch({ type: STAGE_DELETE_RESET })
      }

      if (successParticipantGroupAdd) {
        window.scrollTo(0, 0)
        setToastMessages((toastMessages) => [
          ...toastMessages,
          {
            uid: new Date().valueOf(),
            variant: 'info',
            message: 'Participant Group Added',
          },
        ])
        dispatch({ type: PARTI_GROUP_ADD_RESET })
      }

      if (successParticipantGroupDelete) {
        window.scrollTo(0, 0)
        setToastMessages((toastMessages) => [
          ...toastMessages,
          {
            uid: new Date().valueOf(),
            variant: 'info',
            message: 'Participant Group Deleted',
          },
        ])
        dispatch({ type: PARTI_GROUP_DELETE_RESET })
      }

      if (successEditStudy) {
        window.scrollTo(0, 0)
        setIsSaving(false)
        setStudy(updatedStudy)
        setToastMessages((toastMessages) => [
          ...toastMessages,
          {
            uid: new Date().valueOf(),
            variant: 'info',
            message: 'Study information was changed successfully',
          },
        ])
        dispatch({ type: STUDY_EDIT_EXCEPT_CONSENT_RESET })
      }

      if (successSettingsSetMutation) {
        window.scrollTo(0, 0)
        // Keep the local selection aligned with whichever set the backend returned
        // (e.g. after creating a new set the response focuses on that new set).
        if (updatedStudy && updatedStudy.settingsSetId) {
          setSelectedSettingsSetId(String(updatedStudy.settingsSetId))
        }
        setToastMessages((toastMessages) => [
          ...toastMessages,
          {
            uid: new Date().valueOf(),
            variant: 'info',
            message: 'Settings set updated',
          },
        ])
        dispatch({ type: SETTINGS_SET_MUTATION_RESET })
      }
    }
  }, [
    dispatch,
    history,
    study,
    studyId,
    selectedSettingsSetId,
    systemOwnerInfo,
    logout,
    updatedStudy,
    loadingInitiateStudy,
    loadingQxList,
    loadingEditStudy,
    loadingJitaiMessages,
    loadingJitaiVariables,
    loadingJitaiDecisionRules,
    loadingTxPageList,
    errorInitiateStudy,
    errorQxList,
    errorEditStudy,
    errorJitaiMessages,
    errorJitaiVariables,
    errorJitaiDecisionRules,
    errorTxPageList,
    successInitiateStudy,
    successEditConsent,
    successStageAdd,
    successStageDelete,
    successQxList,
    successParticipantGroupAdd,
    successParticipantGroupDelete,
    successEditStudy,
    successSettingsSetMutation,
    successJitaiMessages,
    successJitaiVariables,
    successJitaiDecisionRules,
    successTxPageList,
    managedByUserId,
  ])

  useEffect(() => {
    if (errorEditStudy) {
      setIsSaving(false)
    }
  }, [errorEditStudy])

  const headingStyle = {
    fontSize: '2rem',
    fontWeight: 'bold',
  }

  const panelStyle = {
    display: 'inline-block',
    height: '100%',
    overflowY: 'scroll',
    overflowX: 'hidden',
    marginRight: '10px',
  }

  const addStageHandler = async () => {
    if (
      window.confirm(
        'This operation will remove unsaved changes. Please save changes first if you have not done that. Are you sure you want to go ahead?'
      )
    ) {
      resetErrorState(dispatch)
      dispatch(addStage(study._id, study.settingsSetId))
    }
  }

  const saveHandler = async () => {
    if (isSaving || loadingEditStudy) return
    setIsSaving(true)
    dispatch(editStudyExceptConsent(study._id, study))
  }

  // Switch which settings set is being edited. Resetting study details triggers a
  // reload for the newly selected set (see the load effect above).
  const settingsSetChangeHandler = (newSetId) => {
    if (String(newSetId) === String(study.settingsSetId)) return
    if (
      window.confirm(
        'Switching settings set will discard unsaved changes. Continue?'
      )
    ) {
      setSelectedSettingsSetId(newSetId)
      dispatch({ type: STUDY_DETAILS_RESET })
    }
  }

  // System-owner-only: create a new settings set by deep-cloning the set being viewed.
  const createSettingsSetHandler = () => {
    const name = window.prompt('Name for the new settings set?')
    if (!name) return
    const labelsRaw = window.prompt(
      'Optional labels (comma-separated), or leave blank:'
    )
    const labels = labelsRaw
      ? labelsRaw
          .split(',')
          .map((l) => l.trim())
          .filter((l) => l.length > 0)
      : []
    resetErrorState(dispatch)
    dispatch(
      createSettingsSet(study._id, {
        name,
        labels,
        sourceSetId: study.settingsSetId,
      })
    )
  }

  // System-owner-only: rename / relabel the settings set currently being viewed.
  const editSettingsSetHandler = () => {
    const current =
      (study.settings_set_options || []).find(
        (o) => String(o._id) === String(study.settingsSetId)
      ) || {}
    const name = window.prompt('Settings set name:', current.name || '')
    if (name === null) return
    const labelsRaw = window.prompt(
      'Labels (comma-separated):',
      (current.labels || []).join(', ')
    )
    if (labelsRaw === null) return
    const labels = labelsRaw
      .split(',')
      .map((l) => l.trim())
      .filter((l) => l.length > 0)
    resetErrorState(dispatch)
    dispatch(updateSettingsSet(study._id, study.settingsSetId, { name, labels }))
  }

  // System-owner-only: mark the current settings set as the study default.
  const setDefaultSettingsSetHandler = () => {
    if (
      window.confirm(
        'Make this settings set the default? New participants (with no explicit set) and default views will use it. Existing enrolments are not moved.'
      )
    ) {
      resetErrorState(dispatch)
      dispatch(setDefaultSettingsSet(study._id, study.settingsSetId))
    }
  }

  // System-owner-only: delete the current settings set (blocked if it is default
  // or has active enrolments, enforced on the backend).
  const deleteSettingsSetHandler = () => {
    if (
      window.confirm(
        'Delete this settings set and its stages/groups? This cannot be undone.'
      )
    ) {
      resetErrorState(dispatch)
      // After deletion the backend returns the default set; realign local selection.
      setSelectedSettingsSetId(undefined)
      dispatch(deleteSettingsSet(study._id, study.settingsSetId))
    }
  }

  const isInitialLoading = loadingInitiateStudy
  const showSavingOverlay = isSaving || loadingEditStudy
  useStudyEditBeforeUnload(showSavingOverlay)

  return (
    <>
      {/* Display toast messages */}
      <div
        style={{
          position: 'absolute',
          backgroundColor: 'transparent',
          pointerEvents: 'none',
          x: '0',
          y: '0',
          height: '80vh',
          width: '80vw',
          zIndex: 10001,
        }}
      >
        {toastMessages.map((toastMessage) => (
          <ToastMessage
            key={toastMessage.uid}
            variant={toastMessage.variant}
            onClose={() => toastOnCloseHandler(toastMessage.uid)}
          >
            {toastMessage.message}
          </ToastMessage>
        ))}
      </div>

      {isInitialLoading ? (
        <StudyEditBlockingOverlay
          title='Loading study'
          message='Please wait while the study editor is loading.'
        />
      ) : (
        <>
          <div style={{ display: 'relative', height: '90vh', width: '100%' }}>
            <Row style={{ minHeight: '60px' }} className='align-items-center'>
              <Col xs='auto'>
                <h1 style={headingStyle}>Edit Study</h1>
              </Col>
              {study.settings_set_options &&
                study.settings_set_options.length > 0 && (
                  <Col xs='auto' className='ml-auto text-right'>
                    <Form.Group
                      controlId='settingsSetSelect'
                      className='d-flex align-items-center justify-content-end mb-0'
                    >
                      <Form.Label className='mb-0 mr-1 text-nowrap'>
                        Settings
                      </Form.Label>
                      <Form.Control
                        as='select'
                        size='sm'
                        value={study.settingsSetId || ''}
                        onChange={(e) =>
                          settingsSetChangeHandler(e.target.value)
                        }
                        disabled={loadingSettingsSetMutation}
                        style={{ width: 'auto' }}
                      >
                        {study.settings_set_options.map((opt) => (
                          <option key={opt._id} value={opt._id}>
                            {opt.name}
                            {opt.is_default ? ' (default)' : ''}
                            {opt.labels && opt.labels.length > 0
                              ? ` [${opt.labels.join(', ')}]`
                              : ''}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                    <div style={{ marginTop: '6px' }}>
                      <Button
                        size='sm'
                        variant='outline-primary'
                        className='mr-1'
                        onClick={createSettingsSetHandler}
                        disabled={loadingSettingsSetMutation}
                      >
                        + New set
                      </Button>
                      <Button
                        size='sm'
                        variant='outline-secondary'
                        className='mr-1'
                        onClick={editSettingsSetHandler}
                        disabled={loadingSettingsSetMutation}
                      >
                        Rename / labels
                      </Button>
                      <Button
                        size='sm'
                        variant='outline-success'
                        className='mr-1'
                        onClick={setDefaultSettingsSetHandler}
                        disabled={loadingSettingsSetMutation}
                      >
                        Set default
                      </Button>
                      <Button
                        size='sm'
                        variant='outline-danger'
                        onClick={deleteSettingsSetHandler}
                        disabled={loadingSettingsSetMutation}
                      >
                        Delete set
                      </Button>
                    </div>
                  </Col>
                )}
              <Col xs={12}>
                {errorInitiateStudy && (
                  <Message variant='danger'>{errorInitiateStudy}</Message>
                )}

                {errorSettingsSetMutation && (
                  <Message variant='danger'>{errorSettingsSetMutation}</Message>
                )}

                {errorEditConsent && (
                  <Message variant='danger'>{errorEditConsent}</Message>
                )}

                {errorStageAdd && (
                  <Message variant='danger'>{errorStageAdd}</Message>
                )}

                {errorStageDelete && (
                  <Message variant='danger'>{errorStageDelete}</Message>
                )}

                {errorParticipantGroupAdd && (
                  <Message variant='danger'>{errorParticipantGroupAdd}</Message>
                )}

                {errorEditStudy && (
                  <Message variant='danger'>{errorEditStudy}</Message>
                )}

                {errorParticipantGroupDelete && (
                  <Message variant='danger'>
                    {errorParticipantGroupDelete}
                  </Message>
                )}
              </Col>
            </Row>

            <div
              style={{
                display: 'block',
                width: '100%',
                height: '85%',
                overflowX: 'scroll',
                overflowY: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              <div style={{ ...panelStyle, width: '300px' }}>
                <Card className='p-3 rounded'>
                  <SystemOwnerStudyBasic study={study}></SystemOwnerStudyBasic>
                </Card>
              </div>

              {study.stage_list.map((stage) => (
                <div
                  style={{ ...panelStyle, width: '400px' }}
                  key={stage.stage_num}
                >
                  <Card className='p-3 rounded'>
                    <SystemOwnerStudyStage
                      studyId={study._id}
                      study={study}
                      stage={study.stage_list[stage.stage_num - 1]}
                      qxList={initialQxList}
                      key={stage.stage_num}
                    ></SystemOwnerStudyStage>
                  </Card>
                </div>
              ))}

              <div style={{ ...panelStyle, width: '300px' }}>
                <Card className='p-3 rounded'>
                  <Button onClick={addStageHandler} disabled={showSavingOverlay}>
                    Add Stage
                  </Button>
                </Card>
              </div>
            </div>
          </div>

          {showSavingOverlay && (
            <StudyEditBlockingOverlay
              title='Saving study'
              message='Saving is in progress. Please do not close this browser window or leave this page until saving completes.'
            />
          )}
        </>
      )}

      {/* Sticky bar for saving changes */}
      {!isInitialLoading && !showSavingOverlay && (
        <Row
        style={{
          position: 'fixed',
          backgroundColor: 'white',
          bottom: 0,
          height: '50px',
          width: '95%',
          zIndex: 1,
        }}
      >
        <Col>
          <Button
            onClick={saveHandler}
            className='my-1 btn-success'
            style={{ float: 'left', marginLeft: '10px' }}
          >
            Save Changes
          </Button>

          <SystemOwnerStudyConsent></SystemOwnerStudyConsent>
        </Col>
      </Row>
      )}
    </>
  )
}

export default SystemOwnerStudyEditScreen
